/* The wind-down: plan, progress, summary.
 *
 * One button runs all of it, so this is where it earns its trust. It shows what is
 * about to happen BEFORE anything does (including what cannot be done, and why),
 * waits for a deliberate click, then shows each step as it runs on a progress bar,
 * and ends on a summary that says what was done, what failed and what is left.
 *
 * The decisions are not here. Which groups can be removed, the order, what a
 * failure does to the rest and what is left over all live in lib/windDown.ts,
 * where they are tested. This only draws them and wires the real services in.
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { allRoleGroups } from '@hird/access';
import { leaveSourceFor } from '../../lib/leaveRecord';
import {
  buildPlan,
  initialSteps,
  progressOf,
  runWindDown,
  type IStep,
  type IWindDownPlan,
  type IWindDownSummary,
} from '../../lib/windDown';
import type { IPerson } from '../../models';
import { qk } from '../../query/keys';
import { useServices } from '../../services/context';
import { Button } from '../ui';
import s from './screens.module.scss';

type Phase = 'inspecting' | 'plan' | 'running' | 'done' | 'error';

const ICON: Record<IStep['state'], string> = {
  waiting: 'ti-circle',
  running: 'ti-loader-2',
  done: 'ti-circle-check',
  skipped: 'ti-circle-minus',
  failed: 'ti-alert-triangle',
};
const COLOUR: Record<IStep['state'], string> = {
  waiting: 'var(--muted, #8a94a6)',
  running: 'var(--accent, #0b5cab)',
  done: 'var(--ok, #1a8f4c)',
  skipped: 'var(--muted, #8a94a6)',
  failed: 'var(--bad, #c0392b)',
};

export function WindDownPanel({
  person,
  people,
  depotName,
  lastDay: initialLastDay,
  onCancel,
  onFinished,
}: {
  person: IPerson;
  people: readonly IPerson[];
  depotName: string;
  /** Their last working day to start from: the one somebody typed in advance, or today.
   *  It is editable in the plan, because the offboarding is not always done on the day. */
  lastDay: string;
  onCancel: () => void;
  onFinished: () => void;
}) {
  const svc = useServices();
  const qc = useQueryClient();

  /* Their last WORKING day, which EZ Leave records and works the leaver's pay out
     from. Not the day this is pressed: shutting somebody down a week late must not
     make them leave a week late. */
  const [lastDay, setLastDay] = useState(initialLastDay);
  const validDay = /^\d{4}-\d{2}-\d{2}$/.test(lastDay) && !Number.isNaN(Date.parse(lastDay));
  const [phase, setPhase] = useState<Phase>('inspecting');
  const [problem, setProblem] = useState<string | null>(null);
  const [plan, setPlan] = useState<IWindDownPlan | null>(null);
  const [removeLicences, setRemoveLicences] = useState(false);
  const [steps, setSteps] = useState<readonly IStep[]>([]);
  const [summary, setSummary] = useState<IWindDownSummary | null>(null);
  const started = useRef(false);

  const skus = useQuery({ queryKey: qk.licences, queryFn: () => svc.licences.skus() });
  const skuName = useMemo(() => {
    const m = new Map((skus.data ?? []).map((x) => [x.id, x.name]));
    return (id: string): string => m.get(id) ?? id;
  }, [skus.data]);

  /* Read what is true, once, before offering anything. Permissions are asked for
     here too, so a consent screen appears now and not halfway through. */
  useEffect(() => {
    let live = true;
    const id = person.graphId;
    if (!id) {
      setProblem('Blueprint has never matched this person to an Entra account, so there is nothing to act on.');
      setPhase('error');
      return undefined;
    }
    void (async () => {
      try {
        await svc.windDown.preflight(false);
        const facts = await svc.windDown.inspect(id);
        if (!live) return;
        setPlan(buildPlan(facts, new Set(allRoleGroups()), skuName));
        setPhase('plan');
      } catch (e) {
        if (!live) return;
        setProblem((e as Error).message);
        setPhase('error');
      }
    })();
    return () => { live = false; };
    // The plan is read once per opening; skuName only changes how names print.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [person.graphId]);

  const start = async (): Promise<void> => {
    if (!plan || started.current || !validDay) return;
    started.current = true;
    const id = person.graphId as string;
    const today = new Date().toISOString().slice(0, 10);

    /* Licence consent is only asked for if licences were ticked, and BEFORE the
       first change. */
    if (removeLicences) {
      try {
        await svc.windDown.preflight(true);
      } catch (e) {
        started.current = false;
        setProblem((e as Error).message);
        setPhase('error');
        return;
      }
    }

    setSteps(initialSteps(plan, { removeLicences }));
    setPhase('running');

    const result = await runWindDown(
      plan,
      { removeLicences },
      {
        blockSignIn: () => svc.windDown.blockSignIn(id),
        revokeSessions: () => svc.windDown.revokeSessions(id),
        revokeHubAccess: async () => { await svc.directory.revokeAll(id); },
        removeFromGroup: (gid) => svc.windDown.removeFromGroup(gid, id),
        closeFolder: async () => {
          const r = await svc.onboarding.offboard(person.upn, person.email);
          if (!r.ok) throw new Error(r.message);
          return r.message;
        },
        markLeaver: () => svc.leave.offboard(leaveSourceFor(person, people, depotName), lastDay),
        removeLicences: (ids) => svc.windDown.removeLicences(id, ids),
        stamp: async (note) => {
          const notes = [person.notes, `${note} Last working day ${lastDay}.`].filter(Boolean).join('\n');
          /* leavingOn becomes the day they confirmed, so Blueprint and EZ Leave hold
             the same last working day. */
          await svc.people.update(person.id, { offboardedOn: today, leavingOn: lastDay, notes });
        },
      },
      (live) => setSteps(live),
      today,
    );

    setSummary(result);
    setPhase('done');
    void qc.invalidateQueries({ queryKey: qk.people });
    void qc.invalidateQueries({ queryKey: qk.audit });
    void qc.invalidateQueries({ queryKey: qk.directoryAccess(id) });
  };

  const removable = plan?.groups.filter((g) => g.verdict === 'remove') ?? [];
  const keeps = plan?.groups.filter((g) => g.verdict === 'keep') ?? [];
  const pct = progressOf(steps);

  const box: React.CSSProperties = {
    border: '1px solid var(--border, #d9dee7)', borderRadius: 12, padding: '12px 14px', margin: '0 0 12px',
  };

  return (
    <div aria-live="polite">
      <style>{'@keyframes wd-spin { to { transform: rotate(360deg); } }'}</style>
      <div className={s.stepRow}>
        <b style={{ fontSize: 16 }}>Offboard {person.name}</b>
        <div className={s.stepDetail}>
          Their sign-in is blocked first; everything after it is tidying.
        </div>
      </div>

      {phase === 'inspecting' ? (
        <div className={s.stepDetail}>Checking their account…</div>
      ) : null}

      {phase === 'error' ? (
        <div className={s.stepRow}>
          <div className={s.stepError}>{problem}</div>
          <Button onClick={onCancel}>Back</Button>
        </div>
      ) : null}

      {phase === 'plan' && plan ? (
        <>
          <div style={box}>
            <label style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
              <b>Last working day</b>
              <input
                type="date"
                value={lastDay}
                onChange={(e) => setLastDay(e.target.value)}
                aria-invalid={!validDay}
                style={{ padding: '6px 10px', borderRadius: 8, border: '1px solid var(--border, #cfd6e2)', font: 'inherit' }}
              />
            </label>
            <span className={validDay ? s.stepDetail : s.stepError} style={{ display: 'block', marginTop: 6 }}>
              {!validDay
                ? 'Enter a date.'
                : lastDay > new Date().toISOString().slice(0, 10)
                  ? 'This is in the future. EZ Leave will mark them a leaver now, with that as their last day.'
                  : 'This is the day EZ Leave records, and their leaver pay is worked out from it. It need not be today.'}
            </span>
          </div>
          {!plan.accountExists ? (
            <div style={box}>
              <b>Their Entra account no longer exists.</b> It has been deleted, so there is nothing to block and no
              groups to remove them from. The rest still needs doing, and is below.
            </div>
          ) : null}
          <div style={box}>
            <b>This will:</b>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {plan.accountExists ? (
                <>
                  <li>{plan.signInEnabled ? 'Block their sign-in and sign them out everywhere' : 'Sign them out everywhere (sign-in is already blocked)'}</li>
                  <li>Take away their hub level and every app role</li>
                  <li>
                    {removable.length === 0
                      ? 'Remove them from no other groups'
                      : `Remove them from ${removable.length} group${removable.length === 1 ? '' : 's'}: ${removable.map((g) => g.name).join(', ')}`}
                  </li>
                </>
              ) : null}
              <li>Close their personal folder (the contents stay)</li>
              <li>Mark them a leaver in EZ Leave, last working day {lastDay || '…'}</li>
            </ul>
          </div>

          {keeps.length > 0 ? (
            <div style={box}>
              <b>This cannot remove, so these stay (and are listed at the end):</b>
              <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
                {keeps.map((g) => <li key={g.id}><b>{g.name}</b>: {g.reason}</li>)}
              </ul>
            </div>
          ) : null}

          <div style={box}>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: plan.licences.length ? 'pointer' : 'default' }}>
              <input
                type="checkbox"
                checked={removeLicences}
                disabled={plan.licences.length === 0}
                onChange={(e) => setRemoveLicences(e.target.checked)}
                style={{ marginTop: 3 }}
              />
              <span>
                <b>
                  {plan.licences.length === 0
                    ? 'No licences are assigned directly'
                    : `Also remove their licences: ${plan.licences.map((l) => l.name).join(', ')}`}
                </b>
                {plan.licences.length > 0 ? (
                  <span className={s.stepDetail} style={{ display: 'block' }}>
                    Off by default. Removing a licence starts a 30-day clock after which their mailbox is
                    deleted, and Blueprint cannot convert it to a shared mailbox first. Only tick this once
                    that is dealt with.
                  </span>
                ) : null}
                {plan.groupLicenceCount > 0 ? (
                  <span className={s.stepDetail} style={{ display: 'block' }}>
                    {plan.groupLicenceCount} more come from a group and go when they leave it.
                  </span>
                ) : null}
              </span>
            </label>
          </div>

          <div className={s.stepRow}>
            <Button primary disabled={!validDay} onClick={() => void start()}>Offboard {person.name.split(' ')[0]}</Button>
            <Button onClick={onCancel}>Cancel</Button>
            <div className={s.stepDetail}>Nothing has been changed yet.</div>
          </div>
        </>
      ) : null}

      {phase === 'running' || phase === 'done' ? (
        <>
          <div
            role="progressbar"
            aria-valuenow={phase === 'done' ? 100 : pct}
            aria-valuemin={0}
            aria-valuemax={100}
            style={{ height: 10, borderRadius: 999, background: 'var(--border, #e3e7ee)', overflow: 'hidden', margin: '4px 0 14px' }}
          >
            <div
              style={{
                width: `${phase === 'done' ? 100 : pct}%`, height: '100%',
                background: phase === 'done' && (summary?.failed ?? 0) > 0 ? 'var(--warn, #d98a00)' : 'var(--accent, #0b5cab)',
                transition: 'width .35s ease',
              }}
            />
          </div>

          {steps.map((st) => (
            <div key={st.id} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', padding: '6px 0' }}>
              <i
                className={`ti ${ICON[st.state]}`}
                aria-hidden
                style={{ color: COLOUR[st.state], fontSize: 20, marginTop: 1, animation: st.state === 'running' ? 'wd-spin 1s linear infinite' : undefined }}
              />
              <div style={{ flex: 1 }}>
                <div><b>{st.label}</b></div>
                <div className={st.state === 'failed' ? s.stepError : s.stepDetail}>{st.detail}</div>
                {st.items.length > 0 ? (
                  <ul style={{ margin: '4px 0 0', paddingLeft: 18 }} className={s.stepDetail}>
                    {st.items.map((it) => <li key={it}>{it}</li>)}
                  </ul>
                ) : null}
              </div>
            </div>
          ))}
        </>
      ) : null}

      {phase === 'done' && summary ? (
        <>
          <div style={{ ...box, marginTop: 12 }}>
            <b>
              {summary.failed === 0
                ? `Offboarded. ${summary.ok} step${summary.ok === 1 ? '' : 's'} done.`
                : `Offboarded with ${summary.failed} problem${summary.failed === 1 ? '' : 's'}. ${summary.ok} step${summary.ok === 1 ? '' : 's'} done.`}
            </b>
            <div className={s.stepDetail}>
              {summary.failed > 0
                ? 'The ones that failed are marked above with the reason. Everything else went ahead.'
                : 'Their sign-in is blocked and their access is gone.'}
            </div>
          </div>
          <div style={box}>
            <b>Still to do:</b>
            <ul style={{ margin: '6px 0 0', paddingLeft: 18 }}>
              {summary.outstanding.map((o) => <li key={o}>{o}</li>)}
            </ul>
          </div>
          {summary.removedGroups.length > 0 ? (
            <div className={s.stepDetail} style={{ marginBottom: 12 }}>
              The groups removed are noted on their record, so they can be put back.
            </div>
          ) : null}
          <div className={s.stepRow}>
            <Button primary onClick={onFinished}>Done</Button>
          </div>
        </>
      ) : null}
    </div>
  );
}
